import React, { useContext, useEffect, useMemo, useState } from "react";
import Drawer from "./Drawer";
import { UserContext } from "../contexts/UserContext";
import { usePushNotifications } from "../hooks/usePushNotifications";
import "./PreferencesDrawer.css";
import Button from "./Button";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import { updateMyUserMeta } from "../services/users";

interface PreferencesDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const isAuthorCommentsEnabled = (raw: unknown): boolean => {
    // opt-out; default ON; "0" means disabled
    return raw !== "0";
};

const isNewBdEnabled = (raw: unknown): boolean => {
    // opt-in; default OFF; "1" means enabled
    return raw === "1";
};

const PreferencesDrawer: React.FC<PreferencesDrawerProps> = ({ isOpen, onClose }) => {
    const { currentUser, setCurrentUser } = useContext(UserContext);
    const push = usePushNotifications();
    const { execute, loading: metaLoading, error: metaError, clearError: clearMetaError } = useAuthenticatedAction();

    const globalEnabled = push.permission === "granted" && push.subscription !== null;
    const globalSwitchDisabled = !push.supported || push.loading;
    const meta = (currentUser?.meta ?? {}) as Record<string, unknown>;
    const typeSwitchesDisabled = !globalEnabled || metaLoading;

    const initialPrefs = useMemo(() => {
        return {
            authorComments: isAuthorCommentsEnabled(meta.birds_push_notify_author_comments),
            newBd: isNewBdEnabled(meta.birds_push_notify_new_bd),
        };
    }, [meta]);

    const [authorComments, setAuthorComments] = useState<boolean>(initialPrefs.authorComments);
    const [newBd, setNewBd] = useState<boolean>(initialPrefs.newBd);

    useEffect(() => {
        setAuthorComments(initialPrefs.authorComments);
        setNewBd(initialPrefs.newBd);
    }, [initialPrefs]);

    if (!currentUser) return null;

    const updateMeta = async (patch: Record<string, string>) => {
        const updated = await execute(updateMyUserMeta, currentUser.id, patch);
        if (updated) {
            setCurrentUser(updated);
        }
    };

    return (
        <Drawer isOpen={isOpen} onClose={onClose} title="Préférences">
            <div className="PreferencesDrawer-content">
                <section className="PreferencesDrawer-section" aria-label="Notifications">
                    <h3 className="PreferencesDrawer-sectionTitle">Notifications</h3>

                    <div className={`PreferencesDrawer-row ${globalSwitchDisabled ? "is-disabled" : ""}`}>
                        <div className="PreferencesDrawer-rowLeft">
                            <div className="PreferencesDrawer-rowTitle">Activer les notifications</div>
                            <div className="PreferencesDrawer-rowSubtitle">
                                {push.supported ? (
                                    globalEnabled ? "Activées sur cet appareil." : "Désactivées sur cet appareil."
                                ) : (
                                    "Non disponible sur ce navigateur ou cet appareil."
                                )}
                            </div>
                            {push.permission === "denied" && (
                                <div className="PreferencesDrawer-rowHint">
                                    Vous avez bloqué les notifications. Autorisez-les dans les réglages du navigateur.
                                </div>
                            )}
                        </div>

                        <label className="PreferencesDrawer-switch">
                            <input
                                type="checkbox"
                                className="PreferencesDrawer-switchInput"
                                checked={globalEnabled}
                                disabled={globalSwitchDisabled || push.permission === "denied"}
                                onChange={() => {
                                    if (globalEnabled) {
                                        void push.unsubscribe();
                                    } else {
                                        void push.subscribe();
                                    }
                                }}
                                aria-label={globalEnabled ? "Désactiver les notifications" : "Activer les notifications"}
                            />
                            <span className="PreferencesDrawer-switchTrack" aria-hidden="true">
                                <span className="PreferencesDrawer-switchThumb" />
                            </span>
                        </label>
                    </div>

                    {push.error && (
                        <p className="PreferencesDrawer-error" role="alert">
                            {push.error}{" "}
                            <button type="button" className="PreferencesDrawer-dismiss" onClick={push.clearError}>
                                Fermer
                            </button>
                        </p>
                    )}

                    <div className="PreferencesDrawer-subsection">
                        <div className="PreferencesDrawer-subsectionTitle">Types de notifications</div>

                        {!globalEnabled && (
                            <p className="PreferencesDrawer-muted">
                                Activez d’abord les notifications globales pour modifier vos préférences.
                            </p>
                        )}

                        <div className={`PreferencesDrawer-row ${typeSwitchesDisabled ? "is-disabled" : ""}`}>
                            <div className="PreferencesDrawer-rowLeft">
                                <div className="PreferencesDrawer-rowTitle">Commentaires sur mes BDs</div>
                                <div className="PreferencesDrawer-rowSubtitle">
                                    {authorComments ? "Activé" : "Désactivé"}
                                </div>
                            </div>
                            <label className="PreferencesDrawer-switch">
                                <input
                                    type="checkbox"
                                    className="PreferencesDrawer-switchInput"
                                    checked={authorComments}
                                    disabled={typeSwitchesDisabled}
                                    onChange={async () => {
                                        const next = !authorComments;
                                        setAuthorComments(next);
                                        try {
                                            await updateMeta({
                                                birds_push_notify_author_comments: next ? "1" : "0",
                                            });
                                        } catch {
                                            setAuthorComments(!next);
                                        }
                                    }}
                                    aria-label="Activer/désactiver les notifications de commentaires"
                                />
                                <span className="PreferencesDrawer-switchTrack" aria-hidden="true">
                                    <span className="PreferencesDrawer-switchThumb" />
                                </span>
                            </label>
                        </div>

                        <div className={`PreferencesDrawer-row ${typeSwitchesDisabled ? "is-disabled" : ""}`}>
                            <div className="PreferencesDrawer-rowLeft">
                                <div className="PreferencesDrawer-rowTitle">Nouvelles BDs publiées</div>
                                <div className="PreferencesDrawer-rowSubtitle">
                                    {newBd ? "Activé" : "Désactivé"}
                                </div>
                            </div>
                            <label className="PreferencesDrawer-switch">
                                <input
                                    type="checkbox"
                                    className="PreferencesDrawer-switchInput"
                                    checked={newBd}
                                    disabled={typeSwitchesDisabled}
                                    onChange={async () => {
                                        const next = !newBd;
                                        setNewBd(next);
                                        try {
                                            await updateMeta({
                                                birds_push_notify_new_bd: next ? "1" : "0",
                                            });
                                        } catch {
                                            setNewBd(!next);
                                        }
                                    }}
                                    aria-label="Activer/désactiver les notifications de nouvelles BDs"
                                />
                                <span className="PreferencesDrawer-switchTrack" aria-hidden="true">
                                    <span className="PreferencesDrawer-switchThumb" />
                                </span>
                            </label>
                        </div>

                        {metaError && (
                            <p className="PreferencesDrawer-error" role="alert">
                                {metaError}{" "}
                                <button type="button" className="PreferencesDrawer-dismiss" onClick={clearMetaError}>
                                    Fermer
                                </button>
                            </p>
                        )}

                        {(metaLoading || push.loading) && (
                            <div className="PreferencesDrawer-loadingRow">
                                <Button variant="secondary" size="small" loading>
                                    Mise à jour…
                                </Button>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </Drawer>
    );
};

export default PreferencesDrawer;

